import { useState } from "react";
import { OperatingFrontChrome } from "@/components/operating-front/OperatingFrontScreens";
import { SalesDeskConfigure } from "./SalesDeskConfigure";
import { SalesDeskHome } from "./SalesDeskHome";
import { SalesDeskLot } from "./salesDeskLot";
import { SalesDeskMatches } from "./SalesDeskMatches.tsx";
import { SalesDeskOrders } from "./salesDeskOrders";
import { SalesDeskPerformance } from "./SalesDeskPerformance";
import { SalesDeskRecord } from "./SalesDeskRecord.tsx";
import { SalesDeskRestock } from "./SalesDeskRestock";
import { SalesDeskStockForm } from "./SalesDeskStockForm.tsx";
import { SalesDeskNav } from "./SalesDeskNav";
import { SalesDeskStart } from "./SalesDeskPages";
import type { SalesDeskPage } from "./salesDeskStyles";
import { salesDeskHandoffEntry, salesDeskSameModelSales, type SalesDeskHistoryEntry } from "./salesDeskSaleRecord";
import {
  salesDeskStockDestination,
  salesDeskStockDraftFromSale,
  submitSalesDeskStockDraft,
  type SalesDeskStockDraft,
  type SalesDeskStockRequest,
} from "./salesDeskStockRequest";
import { useSalesDeskHome } from "./useSalesDeskHome";
import { useSalesDeskOrders } from "./useSalesDeskOrders";

export function SalesDesk() {
  const [page, setPage] = useState<SalesDeskPage>("home");
  const [saleId, setSaleId] = useState<string | null>(null);
  const [stockDraft, setStockDraft] = useState<SalesDeskStockDraft | null>(null);
  const [stockRequests, setStockRequests] = useState<SalesDeskStockRequest[]>([]);
  const [handoffNotes, setHandoffNotes] = useState<Record<string, SalesDeskHistoryEntry[]>>({});
  const home = useSalesDeskHome();
  const loaded = useSalesDeskOrders();
  const sale = loaded.orders.find((order) => order.id === saleId) ?? null;

  function openSale(id: string) {
    setSaleId(id);
    setPage("record");
  }

  function openStockForm(evidenceIds: string[]) {
    if (!sale) return;
    setStockDraft(
      salesDeskStockDraftFromSale(
        sale,
        salesDeskStockDestination(home.locations, home.activeLocationId, home.locationLabel),
        evidenceIds,
      ),
    );
    setPage("stockForm");
  }

  function orderAnotherFromRecord() {
    if (!sale) return;
    const evidenceIds = salesDeskSameModelSales(loaded.orders, sale.id, sale.model, 90, Date.now()).map((row) => row.id);
    openStockForm(evidenceIds);
  }

  return (
    <OperatingFrontChrome showSwitch>
      <div className="pb-24">
        {page === "home" ? (
          <SalesDeskHome
            onStart={() => setPage("start")}
            onOrders={() => setPage("orders")}
            locationLabel={home.locationLabel}
            leads={home.leads}
            leadsLoading={home.leadsLoading}
          />
        ) : null}
        {page === "start" ? <SalesDeskStart onLot={() => setPage("lot")} onConfigure={() => setPage("configure")} /> : null}
        {page === "lot" ? <SalesDeskLot locationLabel={home.locationLabel} onOpenSale={openSale} /> : null}
        {page === "orders" ? <SalesDeskOrders loaded={loaded} onOpen={openSale} /> : null}
        {page === "record" ? (
          <SalesDeskRecord
            sale={sale}
            notes={saleId ? (handoffNotes[saleId] ?? []) : []}
            onMatches={() => setPage("matches")}
            onOrderAnother={orderAnotherFromRecord}
            onHandoff={(action) => {
              if (!saleId) return;
              const entry = salesDeskHandoffEntry(action, new Date().toISOString().slice(0, 10));
              setHandoffNotes((current) => ({ ...current, [saleId]: [entry, ...(current[saleId] ?? [])] }));
            }}
          />
        ) : null}
        {page === "matches" ? (
          <SalesDeskMatches sale={sale} sales={loaded.orders} nowMs={Date.now()} onOrderAnother={openStockForm} />
        ) : null}
        {page === "stockForm" && stockDraft ? (
          <SalesDeskStockForm
            draft={stockDraft}
            locations={home.locations}
            onDraftChange={setStockDraft}
            onBack={() => setPage("record")}
            onSubmit={(attachEvidence) => {
              const result = submitSalesDeskStockDraft(stockDraft, stockRequests, attachEvidence);
              if (!result.ok) return "Name the lot this building is for.";
              setStockRequests((rows) => [...rows, result.request]);
              setStockDraft(null);
              setPage("restock");
              return null;
            }}
          />
        ) : null}
        {page === "configure" ? <SalesDeskConfigure onBack={() => setPage("start")} /> : null}
        {page === "performance" ? <SalesDeskPerformance stockRequests={stockRequests.length} /> : null}
        {page === "restock" ? <SalesDeskRestock requests={stockRequests} onOpenSale={openSale} /> : null}
      </div>
      <SalesDeskNav page={page === "record" || page === "matches" ? "orders" : page} onChange={setPage} />
    </OperatingFrontChrome>
  );
}
